import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { adminService } from '../../../../services';
import { ArrowLeft, AlertTriangle, Gift, Search, Star, ToggleLeft, ToggleRight, Trash2, Plus } from 'lucide-react';
import './Admin.css';

const defaultSupplierForm = {
    supplierName: '',
    selectedSupplierName: '',
    useExistingSupplier: true,
    title: '',
    shortDescription: '',
    fullDescription: '',
    audience: 'Public',
    offerType: 'Supplier',
    offerCode: '',
    ctaLabel: '',
    ctaUrl: '',
    imageUrl: '',
    isFeatured: false,
    isActive: true
};

const AdminPerks = () => {
    const { user } = useUser();
    const [perks, setPerks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState('All');
    const [source, setSource] = useState('All');
    const [supplierForm, setSupplierForm] = useState(defaultSupplierForm);
    const [supplierNames, setSupplierNames] = useState([]);
    const [savingSupplier, setSavingSupplier] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 1
    });

    const fetchPerks = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');
            const params = new URLSearchParams({
                page: String(pagination.page),
                pageSize: String(pagination.pageSize),
                status,
                source
            });

            if (query.trim()) {
                params.append('query', query.trim());
            }

            const data = await adminService.getPerks(params);
            setPerks(data.perks || []);
            setPagination((current) => ({
                ...current,
                totalCount: data.totalCount || 0,
                totalPages: data.totalPages || 1
            }));
        } catch (err) {
            setError(err.message || 'Failed to load perks.');
        } finally {
            setIsLoading(false);
        }
    }, [pagination.page, pagination.pageSize, query, status, source]);

    useEffect(() => {
        fetchPerks();
    }, [fetchPerks]);

    useEffect(() => {
        const loadSupplierNames = async () => {
            try {
                const data = await adminService.getSupplierNames();
                const names = Array.isArray(data) ? data.map((item) => item.name).filter(Boolean) : [];
                setSupplierNames(names);
                if (names.length === 0) {
                    setSupplierForm((prev) => ({ ...prev, useExistingSupplier: false }));
                }
            } catch (err) {
                console.error('Failed to load supplier names:', err);
            }
        };

        loadSupplierNames();
    }, []);

    const handleToggleActive = async (perk) => {
        try {
            if (perk.perkSource === 'Supplier' && perk.supplierPerkId) {
                await adminService.toggleSupplierPerkActive(perk.supplierPerkId);
            } else {
                await adminService.togglePerkActive(perk.clubOfferId);
            }
            await fetchPerks();
        } catch (err) {
            setError(err.message || 'Failed to update active status.');
        }
    };

    const handleToggleFeatured = async (perk) => {
        try {
            if (perk.perkSource === 'Supplier' && perk.supplierPerkId) {
                await adminService.toggleSupplierPerkFeatured(perk.supplierPerkId);
            } else {
                await adminService.togglePerkFeatured(perk.clubOfferId);
            }
            await fetchPerks();
        } catch (err) {
            setError(err.message || 'Failed to update featured status.');
        }
    };

    const handleDelete = async (perk) => {
        if (!window.confirm(`Delete perk "${perk.title}"? This cannot be undone.`)) {
            return;
        }

        try {
            if (perk.perkSource === 'Supplier' && perk.supplierPerkId) {
                await adminService.deleteSupplierPerk(perk.supplierPerkId);
            } else {
                await adminService.deletePerk(perk.clubOfferId);
            }
            await fetchPerks();
        } catch (err) {
            setError(err.message || 'Failed to delete perk.');
        }
    };

    const handleSupplierCreate = async (e) => {
        e.preventDefault();
        const supplierName = supplierForm.useExistingSupplier
            ? supplierForm.selectedSupplierName.trim()
            : supplierForm.supplierName.trim();

        if (!supplierName || !supplierForm.title.trim()) {
            setError('Supplier name and title are required.');
            return;
        }

        try {
            setSavingSupplier(true);
            setError('');
            await adminService.createSupplierPerk({
                supplierName,
                title: supplierForm.title.trim(),
                shortDescription: supplierForm.shortDescription.trim() || null,
                fullDescription: supplierForm.fullDescription.trim() || null,
                audience: supplierForm.audience,
                offerType: supplierForm.offerType,
                offerCode: supplierForm.offerCode.trim() || null,
                ctaLabel: supplierForm.ctaLabel.trim() || null,
                ctaUrl: supplierForm.ctaUrl.trim() || null,
                imageUrl: supplierForm.imageUrl.trim() || null,
                isFeatured: supplierForm.isFeatured,
                isActive: supplierForm.isActive
            });
            setSupplierForm(defaultSupplierForm);
            setSource('All');
            setSupplierNames((current) => current.includes(supplierName) ? current : [...current, supplierName].sort());
            await fetchPerks();
        } catch (err) {
            setError(err.message || 'Failed to create supplier perk.');
        } finally {
            setSavingSupplier(false);
        }
    };

    if (!user?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access this page.</p>
                    <Link to="/dashboard/home" className="admin-btn admin-btn-primary">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <Link to="/dashboard/admin" className="admin-back-btn">
                        <ArrowLeft size={20} />
                    </Link>
                    <div className="admin-header-icon">
                        <Gift size={28} />
                    </div>
                    <div>
                        <h1>Manage Perks</h1>
                        <p>{pagination.totalCount} total perks across venues and suppliers</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="admin-alert admin-alert-error">
                    <AlertTriangle size={20} />
                    {error}
                </div>
            )}

            <div className="admin-equipment-panel" style={{ marginBottom: 20 }}>
                <h3>Create Supplier Perk (Shoot Connect Offer)</h3>
                <form onSubmit={handleSupplierCreate} className="admin-modal-form">
                    <div className="admin-form-checkboxes">
                        <label className="admin-checkbox">
                            <input
                                type="radio"
                                checked={supplierForm.useExistingSupplier}
                                onChange={() => setSupplierForm((prev) => ({ ...prev, useExistingSupplier: true }))}
                            />
                            Use existing supplier
                        </label>
                        <label className="admin-checkbox">
                            <input
                                type="radio"
                                checked={!supplierForm.useExistingSupplier}
                                onChange={() => setSupplierForm((prev) => ({ ...prev, useExistingSupplier: false }))}
                            />
                            Add new supplier
                        </label>
                    </div>
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label>Supplier Name</label>
                            {supplierForm.useExistingSupplier ? (
                                <select
                                    value={supplierForm.selectedSupplierName}
                                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, selectedSupplierName: e.target.value }))}
                                    required
                                    disabled={supplierNames.length === 0}
                                >
                                    <option value="">{supplierNames.length === 0 ? 'No suppliers yet' : 'Select supplier'}</option>
                                    {supplierNames.map((name) => (
                                        <option key={name} value={name}>{name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={supplierForm.supplierName}
                                    onChange={(e) => setSupplierForm((prev) => ({ ...prev, supplierName: e.target.value }))}
                                    placeholder="Brand or supplier"
                                    required
                                />
                            )}
                        </div>
                        <div className="admin-form-group">
                            <label>Perk Title</label>
                            <input
                                type="text"
                                value={supplierForm.title}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, title: e.target.value }))}
                                placeholder="Offer title"
                                required
                            />
                        </div>
                    </div>
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label>Short Description</label>
                            <input
                                type="text"
                                value={supplierForm.shortDescription}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, shortDescription: e.target.value }))}
                                placeholder="One-line summary"
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>Offer Type</label>
                            <select
                                value={supplierForm.offerType}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, offerType: e.target.value }))}
                            >
                                <option value="Supplier">Supplier</option>
                                <option value="Discount">Discount</option>
                                <option value="Coupon">Coupon</option>
                                <option value="Venue">Venue</option>
                            </select>
                        </div>
                    </div>
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label>Full Description</label>
                            <textarea
                                value={supplierForm.fullDescription}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, fullDescription: e.target.value }))}
                                placeholder="Describe the perk, redemption steps, and fine print"
                                rows={4}
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>Audience</label>
                            <select
                                value={supplierForm.audience}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, audience: e.target.value }))}
                            >
                                <option value="Public">Public</option>
                                <option value="Members">Members</option>
                                <option value="Premium">Premium</option>
                            </select>
                        </div>
                    </div>
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label>Offer Code</label>
                            <input
                                type="text"
                                value={supplierForm.offerCode}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, offerCode: e.target.value }))}
                                placeholder="Coupon or redemption code"
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>CTA Label</label>
                            <input
                                type="text"
                                value={supplierForm.ctaLabel}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, ctaLabel: e.target.value }))}
                                placeholder="e.g. Claim with supplier"
                            />
                        </div>
                        <div className="admin-form-group">
                            <label>CTA URL</label>
                            <input
                                type="url"
                                value={supplierForm.ctaUrl}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, ctaUrl: e.target.value }))}
                                placeholder="https://"
                            />
                        </div>
                    </div>
                    <div className="admin-form-row">
                        <div className="admin-form-group">
                            <label>Image URL</label>
                            <input
                                type="url"
                                value={supplierForm.imageUrl}
                                onChange={(e) => setSupplierForm((prev) => ({ ...prev, imageUrl: e.target.value }))}
                                placeholder="Cloudinary image or logo URL"
                            />
                        </div>
                    </div>
                    <div className="admin-form-checkboxes">
                        <label className="admin-checkbox">
                            <input type="checkbox" checked={supplierForm.isFeatured} onChange={(e) => setSupplierForm((prev) => ({ ...prev, isFeatured: e.target.checked }))} />
                            Featured
                        </label>
                        <label className="admin-checkbox">
                            <input type="checkbox" checked={supplierForm.isActive} onChange={(e) => setSupplierForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                            Active now
                        </label>
                    </div>
                    <div className="admin-modal-actions">
                        <button type="submit" className="admin-btn admin-btn-primary" disabled={savingSupplier}>
                            <Plus size={16} />
                            {savingSupplier ? 'Creating...' : 'Create Supplier Perk'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="admin-users-controls">
                <form
                    className="admin-search-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPagination((current) => ({ ...current, page: 1 }));
                        fetchPerks();
                    }}
                >
                    <div className="admin-search-input">
                        <Search size={20} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search perk title, supplier, or venue..."
                        />
                    </div>
                    <select value={source} onChange={(e) => { setSource(e.target.value); setPagination((c) => ({ ...c, page: 1 })); }}>
                        <option value="All">All Sources</option>
                        <option value="Venue">Venue Perks</option>
                        <option value="Supplier">Supplier Perks</option>
                    </select>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setPagination((c) => ({ ...c, page: 1 })); }}>
                        <option value="All">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                    <button type="submit" className="admin-btn admin-btn-primary">Search</button>
                </form>
            </div>

            {isLoading ? (
                <div className="admin-loading">
                    <div className="admin-spinner" />
                    <p>Loading perks...</p>
                </div>
            ) : perks.length === 0 ? (
                <div className="admin-empty-state">
                    <Gift size={64} />
                    <h3>No perks found</h3>
                    <p>Try adjusting search, source, or status filters.</p>
                </div>
            ) : (
                <>
                    <div className="admin-users-table-container">
                        <table className="admin-users-table">
                            <thead>
                                <tr>
                                    <th>Perk</th>
                                    <th>Source</th>
                                    <th>Status</th>
                                    <th>Claims</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {perks.map((perk) => (
                                    <tr key={perk.perkSource === 'Supplier' ? `supplier-${perk.supplierPerkId}` : `venue-${perk.clubOfferId}`}>
                                        <td>
                                            <strong>{perk.title}</strong>
                                            <div className="admin-user-email">{perk.offerType || 'General'} | {perk.audience || 'Public'}</div>
                                            {perk.offerCode ? <div className="admin-user-email">Code: {perk.offerCode}</div> : null}
                                        </td>
                                        <td>{perk.perkSource === 'Supplier' ? `${perk.clubName} (Supplier)` : perk.clubName}</td>
                                        <td>
                                            <div className="admin-user-stats">
                                                <span className={`admin-status-badge ${perk.isActive ? 'verified' : 'unverified'}`}>
                                                    {perk.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                                {perk.isFeatured && (
                                                    <span className="admin-status-badge premium">
                                                        <Star size={12} />
                                                        Featured
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{perk.claimCount}</td>
                                        <td>{new Date(perk.createdAt).toLocaleDateString('en-GB')}</td>
                                        <td>
                                            <div className="admin-user-actions">
                                                <button className="admin-btn admin-btn-icon" onClick={() => handleToggleActive(perk)} title="Toggle active">
                                                    {perk.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                </button>
                                                <button className="admin-btn admin-btn-icon" onClick={() => handleToggleFeatured(perk)} title="Toggle featured">
                                                    <Star size={16} />
                                                </button>
                                                <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(perk)} title="Delete perk">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="admin-pagination">
                        <button
                            className="admin-btn admin-btn-secondary"
                            disabled={pagination.page <= 1}
                            onClick={() => setPagination((current) => ({ ...current, page: current.page - 1 }))}
                        >
                            Previous
                        </button>
                        <div className="admin-pagination-info">Page {pagination.page} of {Math.max(1, pagination.totalPages)}</div>
                        <button
                            className="admin-btn admin-btn-secondary"
                            disabled={pagination.page >= pagination.totalPages}
                            onClick={() => setPagination((current) => ({ ...current, page: current.page + 1 }))}
                        >
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminPerks;
